import { FC, useMemo } from 'react'
import classNames from 'classnames'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron.svg'
import styles from './pagination.module.scss'

interface PaginationProps {
  currentPage: number
  onPageChange: (page: number) => void
  totalPages: number
}

export const Pagination: FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  totalPages,
}) => {
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 7

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (currentPage <= 3) {
        // Show 1, 2, 3, ..., last
        pages.push(2, 3, '...', totalPages)
      } else if (currentPage >= totalPages - 2) {
        // Show 1, ..., last-2, last-1, last
        pages.push('...', totalPages - 2, totalPages - 1, totalPages)
      } else {
        // Show 1, ..., current-1, current, current+1, ..., last
        pages.push(
          '...',
          currentPage - 1,
          currentPage,
          currentPage + 1,
          '...',
          totalPages
        )
      }
    }

    return pages
  }, [currentPage, totalPages])

  if (totalPages <= 1) return null

  return (
    <div className={styles.pagination}>
      <button
        className={classNames(styles.navButton, {
          [styles.disabled]: currentPage === 1,
        })}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        type="button"
      >
        <ChevronIcon className={styles.chevronLeft} />
        <span>Previous</span>
      </button>

      <div className={styles.pageNumbers}>
        {pageNumbers.map((page, index) =>
          typeof page === 'string' ? (
            <span className={styles.ellipsis} key={`ellipsis-${index}`}>
              {page}
            </span>
          ) : (
            <button
              className={classNames(styles.pageButton, {
                [styles.active]: page === currentPage,
              })}
              key={page}
              onClick={() => onPageChange(page)}
              type="button"
            >
              {page}
            </button>
          )
        )}
      </div>

      <button
        className={classNames(styles.navButton, {
          [styles.disabled]: currentPage === totalPages,
        })}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        type="button"
      >
        <span>Next</span>
        <ChevronIcon className={styles.chevronRight} />
      </button>
    </div>
  )
}
